import React from 'react';
import { ButtonV2, DialogV2, DrawerV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

/**
 * How the user stayed: the "Keep Editing" button, or closing the dialog (backdrop click or Escape).
 * Telemetry only -- both leave the current draft open the same way.
 */
export type StartBasicDigitalCancelSource = 'button' | 'dismiss';

type StartBasicDigitalConfirmProps = Readonly<{
  isOpen: boolean;
  /**
   * The new draft is being saved, created and opened. The dialog stays up with a spinner on the
   * confirm button until the page moves on, and cannot be dismissed meanwhile -- closing it would
   * not stop the new draft.
   */
  isStarting: boolean;
  /**
   * The event's details the new draft is filled with are still loading, so it cannot start yet. The
   * confirm button spins and does nothing, but the user can still back out.
   */
  isPreparing: boolean;
  onConfirm: () => void;
  onCancel: (source: StartBasicDigitalCancelSource) => void;
}>;

/**
 * Asks before the format switcher leaves this draft for a new Basic Digital one. Basic Digital is not
 * a switch of this draft (see `useStartBasicDigitalDraft`), so the user is told a new card starts
 * from the original design and this one is saved as a draft.
 */
export const StartBasicDigitalConfirm = ({ isOpen, isStarting, isPreparing, onConfirm, onCancel }: StartBasicDigitalConfirmProps) => {
  const { tq } = useTranslation('stationery');
  const isMobileOrTablet = useIsMobileOrTablet();
  const onClose = () => {
    if (!isStarting) onCancel('dismiss');
  };

  const title = tq`Start a new Basic Digital card?`;
  const description = tq`We’ll start a new Basic Digital draft from the original design. Your current card will be saved as a draft.`;

  const confirmButton = (
    <ButtonV2
      shape="rounded"
      intent="neutral"
      size={isMobileOrTablet ? 'lg' : 'md'}
      fullWidth={isMobileOrTablet}
      loading={isStarting || isPreparing}
      onClick={() => {
        if (!isStarting && !isPreparing) onConfirm();
      }}
    >
      {tq`Start New Draft`}
    </ButtonV2>
  );
  const cancelButton = (
    <ButtonV2
      shape="rounded"
      variant="outline"
      intent="neutral"
      size={isMobileOrTablet ? 'lg' : 'md'}
      fullWidth={isMobileOrTablet}
      disabled={isStarting}
      onClick={() => onCancel('button')}
    >
      {tq`Keep Editing`}
    </ButtonV2>
  );

  if (isMobileOrTablet) {
    return (
      <DrawerV2
        isOpen={isOpen}
        onClose={onClose}
        anchor="bottom"
        size="auto"
        useBackdrop
        enableReturnFocusOnClose
      >
        <Flex
          flexDirection="column"
          gap={3}
          paddingX={6}
          paddingTop={6}
          paddingBottom={8}
        >
          <TextV2 typographyVariant="hed2">{title}</TextV2>
          <TextV2
            typographyVariant="body1"
            color="mono10"
            paddingBottom={4}
          >
            {description}
          </TextV2>
          {confirmButton}
          {cancelButton}
        </Flex>
      </DrawerV2>
    );
  }

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      enableReturnFocusOnClose
    >
      <DialogV2.Header>
        <TextV2 typographyVariant="hed3">{title}</TextV2>
      </DialogV2.Header>
      <DialogV2.Body>
        <TextV2
          typographyVariant="body1"
          color="mono10"
        >
          {description}
        </TextV2>
      </DialogV2.Body>
      <DialogV2.Footer
        justifyContent="flex-end"
        gap={3}
      >
        {cancelButton}
        {confirmButton}
      </DialogV2.Footer>
    </DialogV2>
  );
};
